import { createBrowserRouter, Navigate } from 'react-router'

import { AssessmentListContainer } from '@/features/assessments/containers/assessment-list-container'

import { RouteErrorBoundary } from './route-error-boundary'
import { RootHydrateFallback, RootLayout } from './root-layout'
import { AppRoute, paths } from './routes'

export const router = createBrowserRouter([
  {
    Component: RootLayout,
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: RootHydrateFallback,
    children: [
      {
        path: '/',
        element: <Navigate to={paths.root()} replace />
      },
      {
        path: AppRoute.Root,
        element: <AssessmentListContainer />
      },
      {
        path: AppRoute.AssessmentDetail,
        // The workspace is version-agnostic (the asset tree, locations,
        // ARA, documents and tabs behave identically under both scoping
        // formats); only the per-asset scoping surface branches by the
        // row's scopingSchemaVersion — a mount-time decision inside the
        // workspace, not a route-level one.
        lazy: () =>
          import('@/features/plant-assessment/containers/assessment-detail-layout').then(m => ({
            Component: m.AssessmentDetailLayout
          })),
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/plant-assessment/containers/plant-assessment-container/index').then(
                m => ({
                  Component: m.PlantAssessmentContainer
                })
              )
          },
          {
            path: 'analytics/:assessmentAssetId',
            lazy: () =>
              import('@/features/demo/containers/demo-container').then(m => ({
                Component: m.DemoContainer
              })),
            children: [
              {
                index: true,
                lazy: () =>
                  import('@/features/demo/containers/demo-analytics-container').then(m => ({
                    Component: m.DemoAnalyticsContainer
                  }))
              },
              {
                path: 'health-report',
                lazy: () =>
                  import('@/features/demo/containers/demo-health-report-container').then(m => ({
                    Component: m.DemoHealthReportContainer
                  }))
              },
              {
                path: 'spectrum/:sampleId',
                lazy: () =>
                  import('@/features/demo/containers/demo-spectrum-container').then(m => ({
                    Component: m.DemoSpectrumContainer
                  }))
              }
            ]
          }
        ]
      },
      {
        path: AppRoute.MediaDashboard,
        lazy: () =>
          import('@/features/plant-assessment/containers/dashboards-container').then(m => ({
            Component: m.DashboardsContainer
          }))
      },
      {
        path: AppRoute.Matrix,
        lazy: () =>
          import('@/features/matrix/containers/matrix-page').then(m => ({
            Component: m.MatrixPage
          }))
      },
      {
        path: '/plant-assessment/*',
        element: <Navigate to={paths.root()} replace />
      }
    ]
  },
  { path: '*', element: null }
])

/**
 * Anonymous public router tree (ADR 006). Mounted in `App.tsx` as a sibling
 * OUTSIDE `AuthProvider`, so an unauthenticated visitor reaching
 * `/document/:docId` is never redirected to login. The authed `router` above
 * tightens its catch-all to `/plant-assessment/*` so it does not swallow this
 * route. Each `RouterProvider` renders nothing for URLs its own routes do not
 * match, so the two trees coexist without conflict. No `RootLayout` here — the
 * public viewer renders full-viewport with no app chrome.
 */
export const publicRouter = createBrowserRouter([
  {
    path: AppRoute.PublicDocument,
    ErrorBoundary: RouteErrorBoundary,
    lazy: () =>
      import('@/features/public-documents/containers/public-document').then(m => ({
        Component: m.PublicDocument
      }))
  },
  {
    path: '*',
    element: null
  }
])
